import React, { useCallback, useId, useMemo, useState } from 'react'

import {
  Text,
  IconButton,
  Box,
  Grid,
  GridItem,
  Flex,
  Button,
  Stack,
  VisuallyHiddenInput,
} from '@chakra-ui/react'
import { Message } from 'src/components/__legacy__/message/message'
import {
  ArrowDown,
  ArrowUp,
  ItemMoveLeft,
  ItemMoveRight,
} from 'src/components/icon'

import { IcfDocumentForList } from '../../icf-document/entity'
import { IcfDocumentUidAndIndex } from '../entity'

const MAX_DOCUMENT_COUNT = 20

type Props = {
  icfDocuments: IcfDocumentForList[]
  selectedIcfDocuments: IcfDocumentUidAndIndex[]
  onChangeSelectedDocuments: (documents: IcfDocumentUidAndIndex[]) => void
  errorMessage?: string
}

export const SortableMultiSelectRenewal: React.FC<Props> = ({
  icfDocuments,
  selectedIcfDocuments,
  onChangeSelectedDocuments,
  errorMessage,
}) => {
  const [selectedUidsToAttach, setSelectedUidsToAttach] = useState<string[]>([])

  const [selectedUidToDetach, setSelectedUidToDetach] = useState<
    string | undefined
  >()

  const docMap = new Map(icfDocuments.map(doc => [doc.uid, doc]))

  const unselectedDocuments = useMemo(
    () =>
      icfDocuments.filter(
        doc => !selectedIcfDocuments.map(doc => doc.uid).includes(doc.uid),
      ),
    [icfDocuments, selectedIcfDocuments],
  )

  const onCheckToAttach = useCallback(
    (uid: string) => {
      if (selectedUidsToAttach.includes(uid)) {
        setSelectedUidsToAttach(selectedUidsToAttach.filter(i => i !== uid))
        return
      }

      setSelectedUidsToAttach([...selectedUidsToAttach, uid])
    },
    [selectedUidsToAttach],
  )

  const onCheckToDetach = useCallback(
    (uid: string) => {
      if (selectedUidToDetach === uid) {
        setSelectedUidToDetach(undefined)
        return
      }

      setSelectedUidToDetach(uid)
    },
    [selectedUidToDetach],
  )

  const onMoveToAttach = useCallback(() => {
    const newItems: IcfDocumentUidAndIndex[] = []
    const nextIndex =
      selectedIcfDocuments.length > 0
        ? Math.max(...selectedIcfDocuments.map(i => i.index)) + 1
        : 0

    selectedUidsToAttach.forEach((uid, i) => {
      const item = icfDocuments.find(doc => doc.uid === uid)
      if (!item) {
        return
      }

      newItems.push({ uid, index: nextIndex + i })
    })

    onChangeSelectedDocuments([...selectedIcfDocuments, ...newItems])
    setSelectedUidsToAttach([])
  }, [
    icfDocuments,
    onChangeSelectedDocuments,
    selectedIcfDocuments,
    selectedUidsToAttach,
  ])

  const onMoveToDetach = useCallback(() => {
    const item = icfDocuments.find(doc => doc.uid === selectedUidToDetach)
    if (!item) {
      return
    }

    //残った選択済みアイテムのindexは連番になるようにしておく
    onChangeSelectedDocuments(
      selectedIcfDocuments
        .filter(doc => doc.uid !== item.uid)
        .sort((a, b) => a.index - b.index)
        .map((doc, i) => ({ ...doc, index: i })),
    )
    setSelectedUidToDetach(undefined)
  }, [
    selectedUidToDetach,
    selectedIcfDocuments,
    icfDocuments,
    onChangeSelectedDocuments,
  ])

  const onBulkDetach = useCallback(() => {
    onChangeSelectedDocuments([])
    setSelectedUidToDetach(undefined)
  }, [onChangeSelectedDocuments])

  const onUp = useCallback(() => {
    const selectedItem = selectedIcfDocuments.find(
      doc => doc.uid === selectedUidToDetach,
    )
    if (!selectedItem) {
      return
    }

    const targetItem = selectedIcfDocuments.find(
      doc => doc.index === selectedItem.index - 1,
    )
    if (!targetItem) {
      return
    }

    const restItems = selectedIcfDocuments.filter(
      doc => ![selectedItem.uid, targetItem.uid].includes(doc.uid),
    )

    onChangeSelectedDocuments([
      { ...selectedItem, index: targetItem.index },
      { ...targetItem, index: selectedItem.index },
      ...restItems,
    ])
  }, [selectedUidToDetach, onChangeSelectedDocuments, selectedIcfDocuments])

  const onDown = useCallback(() => {
    const selectedItem = selectedIcfDocuments.find(
      doc => doc.uid === selectedUidToDetach,
    )
    if (!selectedItem) {
      return
    }

    const targetItem = selectedIcfDocuments.find(
      doc => doc.index === selectedItem.index + 1,
    )
    if (!targetItem) {
      return
    }

    const restItems = selectedIcfDocuments.filter(
      doc => ![selectedItem.uid, targetItem.uid].includes(doc.uid),
    )

    onChangeSelectedDocuments([
      { ...selectedItem, index: targetItem.index },
      { ...targetItem, index: selectedItem.index },
      ...restItems,
    ])
  }, [selectedUidToDetach, onChangeSelectedDocuments, selectedIcfDocuments])

  const isOverMax = useMemo(() => {
    return (
      selectedUidsToAttach.length + selectedIcfDocuments.length >
      MAX_DOCUMENT_COUNT
    )
  }, [selectedUidsToAttach, selectedIcfDocuments])

  const canAttach = useMemo(() => {
    if (selectedUidsToAttach.length === 0) {
      return false
    }
    if (isOverMax) {
      return false
    }

    return true
  }, [selectedUidsToAttach, isOverMax])

  const canUp = useMemo(() => {
    const selected = selectedIcfDocuments.find(
      doc => doc.uid === selectedUidToDetach,
    )
    if (!selected) {
      return false
    }

    const lowerIndexDoc = selectedIcfDocuments.find(
      doc => doc.index < selected.index,
    )

    return !!lowerIndexDoc
  }, [selectedIcfDocuments, selectedUidToDetach])

  const canDown = useMemo(() => {
    const selected = selectedIcfDocuments.find(
      doc => doc.uid === selectedUidToDetach,
    )
    if (!selected) {
      return false
    }

    const higherIndexDoc = selectedIcfDocuments.find(
      doc => doc.index > selected.index,
    )
    return !!higherIndexDoc
  }, [selectedIcfDocuments, selectedUidToDetach])

  return (
    <Box w="full">
      <Grid
        templateColumns="minmax(240px, 400px) 44px minmax(240px, 400px) 44px"
        alignItems="center"
        rowGap={1.5}
      >
        <GridItem gridColumnStart={1} gridRowStart={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            未設定文書
          </Text>
        </GridItem>
        <GridItem
          gridColumnStart={1}
          gridRowStart={2}
          border="1px"
          borderColor="gray.200"
          borderRadius="base"
          h="240px"
          position="relative"
          p="1"
          overflow="auto"
        >
          {unselectedDocuments.map(doc => (
            <DocOption
              key={doc.uid}
              doc={doc}
              isChecked={selectedUidsToAttach.includes(doc.uid)}
              isDisabled={selectedIcfDocuments.length === MAX_DOCUMENT_COUNT}
              onChange={onCheckToAttach}
            />
          ))}
        </GridItem>

        <GridItem gridColumnStart={2} gridRowStart={2}>
          <Flex align="center" direction="column" gap={2}>
            <IconButton
              aria-label="move-right"
              variant="customIconButtonGhost"
              icon={<ItemMoveRight />}
              onClick={onMoveToAttach}
              isDisabled={!canAttach}
            />
            <IconButton
              aria-label="move-left"
              variant="customIconButtonGhost"
              icon={<ItemMoveLeft />}
              onClick={onMoveToDetach}
              isDisabled={!selectedUidToDetach}
            />
          </Flex>
        </GridItem>

        <GridItem gridColumnStart={3} gridRowStart={1}>
          <Text fontWeight="medium" color="gray.600" fontSize="sm">
            設定文書
          </Text>
        </GridItem>
        <GridItem
          gridColumnStart={3}
          gridRowStart={2}
          border="1px"
          borderColor="gray.200"
          borderRadius="base"
          h="240px"
          position="relative"
          p="1"
          overflow="auto"
        >
          {selectedIcfDocuments
            .sort((a, b) => a.index - b.index)
            .map(selectedDoc => {
              const doc = docMap.get(selectedDoc.uid)
              if (!doc) {
                return null
              }

              return (
                <DocOption
                  key={selectedDoc.uid}
                  doc={doc}
                  isChecked={selectedUidToDetach === doc.uid}
                  onChange={onCheckToDetach}
                />
              )
            })}
        </GridItem>

        <GridItem gridColumnStart={4} gridRowStart={2}>
          <Flex direction="column" gap={2} align="center">
            <IconButton
              aria-label="sort-up"
              variant="customIconButtonGhost"
              icon={<ArrowUp />}
              onClick={onUp}
              isDisabled={!canUp}
            />
            <IconButton
              aria-label="sort-down"
              variant="customIconButtonGhost"
              icon={<ArrowDown />}
              onClick={onDown}
              isDisabled={!canDown}
            />
          </Flex>
        </GridItem>

        <GridItem gridRowStart={3}>
          <Stack spacing="1">
            {!!errorMessage && (
              <Text color="red.500" fontSize="sm">
                {errorMessage}
              </Text>
            )}
            {(isOverMax ||
              selectedIcfDocuments.length === MAX_DOCUMENT_COUNT) && (
              <Message
                type="warning"
                message={`登録できる文書の上限は${MAX_DOCUMENT_COUNT}件です`}
              />
            )}
          </Stack>
        </GridItem>
        <GridItem gridRowStart={3} gridColumnStart={3}>
          <Flex mt="2" justify="end">
            <Button variant="text" onClick={onBulkDetach}>
              一括解除
            </Button>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  )
}

type OptionProps = {
  doc: IcfDocumentForList
  isChecked: boolean
  isDisabled?: boolean
  onChange: (docUid: string) => void
}
const DocOption: React.FC<OptionProps> = ({
  doc,
  isChecked,
  isDisabled,
  onChange,
}) => {
  const id = useId()
  return (
    <Box
      as="label"
      htmlFor={id}
      cursor="pointer"
      display="block"
      w="full"
      p="1"
      fontSize="sm"
      color={doc.status === 'Disabled' ? 'gray.400' : 'gray.800'}
      bg={isChecked ? 'blue.100' : undefined}
      _hover={{ bg: isChecked ? 'blue.100' : 'blue.50' }}
    >
      <VisuallyHiddenInput
        id={id}
        type="checkbox"
        value={doc.uid}
        checked={isChecked}
        onChange={() => onChange(doc.uid)}
        disabled={isDisabled}
      />
      {`${doc.numberingId}. ${doc.name}`}
    </Box>
  )
}
